import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
  duration?: number;
  threshold?: number;
}

export const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  delay = 0,
  direction = 'up',
  duration = 600,
  threshold = 0.1,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Start visible when motion is unwanted or unobservable. The hidden state is
  // `opacity-0`, so anything that stops the observer from firing leaves the
  // content permanently invisible rather than merely un-animated. That is a
  // content-loss failure dressed as an animation, and it is worth being
  // conservative about: CapabilityGrid and PractitionerBand wrap every card in
  // one of these, so a page can be most of a screen of nothing.
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (typeof IntersectionObserver === 'undefined') return true;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  });

  useEffect(() => {
    if (isVisible) return;

    const el = ref.current;
    if (!el) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold }
    );
    observer.observe(el);

    // Backstop: if the observer has not fired within a few seconds the element
    // is either off-screen in a way we cannot detect or the observer is not
    // working. Showing it is always better than hiding it.
    const backstop = window.setTimeout(() => setIsVisible(true), 4000);

    return () => {
      observer.disconnect();
      window.clearTimeout(backstop);
    };
  }, [delay, threshold, isVisible]);

  const getTransformClasses = () => {
    const base = 'transition-all ease-out';
    const durationClass = `duration-[${duration}ms]`;
    
    if (!isVisible) {
      switch (direction) {
        case 'up':
          return `${base} ${durationClass} translate-y-8 opacity-0`;
        case 'down':
          return `${base} ${durationClass} -translate-y-8 opacity-0`;
        case 'left':
          return `${base} ${durationClass} translate-x-8 opacity-0`;
        case 'right':
          return `${base} ${durationClass} -translate-x-8 opacity-0`;
        case 'fade':
          return `${base} ${durationClass} opacity-0`;
        default:
          return `${base} ${durationClass} translate-y-8 opacity-0`;
      }
    }
    
    return `${base} ${durationClass} translate-x-0 translate-y-0 opacity-100`;
  };

  return (
    <div ref={ref} className={cn(getTransformClasses(), className)}>
      {children}
    </div>
  );
};